import { useState, useEffect } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiGet } from '../lib/api';
import { useCurrency } from '../context/CurrencyContext';
import { 
  CheckCircle, XCircle, Calendar, Clock, User, Coffee, 
  Video, MapPin, Shield, CreditCard, ArrowRight, MessageSquare,
  FileText, Loader2
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export default function Booking() {
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Currency conversion hook
  const { formatPrice, convert, currency: userCurrency } = useCurrency();
  
  // Convert and format currency using user's preferred currency
  const formatAmount = (amount, sourceCurrency = 'SEK') => {
    const converted = convert(amount, sourceCurrency, userCurrency);
    return formatPrice(converted, userCurrency);
  };
  
  const bookingId = params?.id || '';
  const searchParams = new URLSearchParams(location.search || '');
  const paid = searchParams.get('paid') === '1';
  const canceled = searchParams.get('canceled') === '1';
  
  const [booking, setBooking] = useState(null);
  const [creative, setCreative] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch booking details when we have an ID
  useEffect(() => {
    if (!bookingId) return;
    
    async function fetchBooking() {
      setLoading(true);
      setError('');
      try {
        const res = await apiGet(`/bookings/${bookingId}`);
        const bookingData = res?.data?.booking || res?.booking || res?.data || res;
        
        // Normalize field names (handle both old and new naming)
        const normalized = {
          ...bookingData,
          // Ensure we have the correct field names
          total_amount: bookingData.total_amount || bookingData.total_price || 0,
          start_at: bookingData.start_at || bookingData.scheduled_at,
          end_at: bookingData.end_at,
          client_id: bookingData.client_id || bookingData.buyer_id,
          creative_id: bookingData.creative_id || bookingData.talent_id,
          escrow_amount: bookingData.escrow_amount || bookingData.creative_amount || 0,
          creative_amount: bookingData.creative_amount || bookingData.escrow_amount || 0,
        };
        
        setBooking(normalized);
        
        // Fetch creative details if available
        const creativeId = normalized.creative_id;
        if (creativeId) {
          try {
            const creativeRes = await apiGet(`/users/${creativeId}`);
            setCreative(creativeRes?.data?.user || creativeRes?.user || creativeRes?.data || creativeRes);
          } catch (e) {
            console.error('Failed to fetch creative:', e);
          }
        }
      } catch (e) {
        setError(e?.response?.data?.error || e?.message || 'Failed to load booking');
      } finally {
        setLoading(false);
      }
    }
    
    fetchBooking();
  }, [bookingId]);

  // If no booking ID, redirect to dashboard
  if (!bookingId) {
    return (
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto text-center space-y-4">
          <h1 className="text-2xl font-semibold text-primary-text">No Booking Found</h1>
          <p className="text-secondary-text">Please select a booking to view.</p>
          <Link to="/dashboard/client/bookings" className="btn btn-primary">
            View My Bookings
          </Link>
        </div>
      </section>
    );
  }

  // Payment canceled
  if (canceled) {
    return (
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-lg mx-auto">
          <div className="card p-8 text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-red-500/10 rounded-full flex items-center justify-center">
              <XCircle size={40} className="text-red-500" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-primary-text">Payment Canceled</h1>
              <p className="text-secondary-text">
                Your payment was canceled. The booking has not been confirmed.
              </p>
            </div>

            <div className="bg-subtle-bg rounded-lg p-4 text-left border border-card-border">
              <div className="text-sm text-secondary-text">Booking Reference</div>
              <div className="font-mono text-primary-text">{bookingId.slice(0, 8)}...</div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                onClick={() => navigate(-1)}
                className="flex-1 btn btn-primary flex items-center justify-center gap-2"
              >
                Try Again
                <ArrowRight size={16} />
              </button>
              <Link 
                to="/services" 
                className="flex-1 btn btn-outline flex items-center justify-center gap-2"
              >
                Browse Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Loading state
  if (loading) {
    return (
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-lg mx-auto text-center space-y-4">
          <Loader2 size={40} className="animate-spin mx-auto text-blue-500" />
          <p className="text-secondary-text">Loading booking details...</p>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-lg mx-auto">
          <div className="card p-8 text-center space-y-6">
            <div className="w-20 h-20 mx-auto bg-red-500/10 rounded-full flex items-center justify-center">
              <XCircle size={40} className="text-red-500" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-primary-text">Error Loading Booking</h1>
              <p className="text-secondary-text">{error}</p>
            </div>
            <Link to="/dashboard/client/bookings" className="btn btn-primary">
              View My Bookings
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Payment success - show full booking details
  if (paid && booking) {
    const isFika = booking.meeting_type?.includes('fika');
    const isOnline = booking.meeting_type === 'fika_online';
    const escrowAmount = booking.escrow_amount || booking.creative_amount || 0;
    const hasEscrow = escrowAmount > 0;

    return (
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Success Header */}
          <div className="card p-8 text-center space-y-4">
            <div className="w-20 h-20 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold text-primary-text">
                {isFika ? 'Fika Booked Successfully!' : 'Booking Confirmed!'}
              </h1>
              <p className="text-secondary-text">
                Your payment was successful and your booking is confirmed.
              </p>
            </div>

            {/* Booking Number */}
            <div className="inline-flex items-center gap-2 bg-green-500/10 text-green-600 dark:text-green-400 px-4 py-2 rounded-full text-sm font-medium">
              <FileText size={16} />
              <span>Booking #{booking.booking_number || bookingId.slice(0, 8)}</span>
            </div>
          </div>

          {/* Booking Details Card */}
          <div className="card p-6 space-y-6">
            <h2 className="text-lg font-semibold text-primary-text flex items-center gap-2">
              <Calendar size={20} className="text-blue-500" />
              Booking Details
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              {/* Creative Info */}
              {creative && (
                <div className="flex items-center gap-3 p-4 bg-subtle-bg rounded-lg border border-card-border">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center overflow-hidden">
                    {creative.avatar_url ? (
                      <img src={creative.avatar_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <User size={24} className="text-white" />
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-primary-text">
                      {creative.display_name || creative.full_name || creative.email?.split('@')[0]}
                    </div>
                    <div className="text-sm text-secondary-text">Creative</div>
                  </div>
                </div>
              )}

              {/* Meeting Type */}
              <div className="p-4 bg-subtle-bg rounded-lg border border-card-border">
                <div className="flex items-center gap-2 text-secondary-text text-sm mb-1">
                  {isFika ? (
                    isOnline ? <Video size={16} /> : <MapPin size={16} />
                  ) : (
                    <Coffee size={16} />
                  )}
                  <span>Meeting Type</span>
                </div>
                <div className="font-medium text-primary-text">
                  {booking.meeting_type === 'fika_online' && 'Online Fika'}
                  {booking.meeting_type === 'fika_in_person' && 'In-Person Fika'}
                  {booking.meeting_type === 'hourly' && 'Hourly Session'}
                  {booking.meeting_type === 'package' && 'Package'}
                  {!['fika_online', 'fika_in_person', 'hourly', 'package'].includes(booking.meeting_type) && (booking.meeting_type || 'Service')}
                </div>
              </div>

              {/* Date */}
              <div className="p-4 bg-subtle-bg rounded-lg border border-card-border">
                <div className="flex items-center gap-2 text-secondary-text text-sm mb-1">
                  <Calendar size={16} />
                  <span>Date</span>
                </div>
                <div className="font-medium text-primary-text">
                  {formatDate(booking.start_at)}
                </div>
              </div>

              {/* Time */}
              <div className="p-4 bg-subtle-bg rounded-lg border border-card-border">
                <div className="flex items-center gap-2 text-secondary-text text-sm mb-1">
                  <Clock size={16} />
                  <span>Time</span>
                </div>
                <div className="font-medium text-primary-text">
                  {formatTime(booking.start_at)} - {formatTime(booking.end_at)}
                </div>
              </div>
            </div>

            {/* Notes */}
            {booking.notes && (
              <div className="p-4 bg-subtle-bg rounded-lg border border-card-border">
                <div className="text-secondary-text text-sm mb-1">Notes</div>
                <div className="text-primary-text">{booking.notes}</div>
              </div>
            )}
          </div>

          {/* Payment Summary Card */}
          <div className="card p-6 space-y-4">
            <h2 className="text-lg font-semibold text-primary-text flex items-center gap-2">
              <CreditCard size={20} className="text-blue-500" />
              Payment Summary
            </h2>

            <div className="space-y-3">
              {booking.platform_fee > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-secondary-text">
                    {isFika ? 'Fika Fee (refreshments)' : 'Platform Fee'}
                  </span>
                  <span className="text-primary-text">
                    {formatAmount(booking.platform_fee, booking.currency)}
                  </span>
                </div>
              )}
              
              {(booking.creative_amount || booking.escrow_amount) > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-secondary-text">Creative's Fee</span>
                  <span className="text-primary-text">
                    {formatAmount(booking.creative_amount || booking.escrow_amount, booking.currency)}
                  </span>
                </div>
              )}

              <div className="border-t border-card-border pt-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-primary-text">Total Paid</span>
                  <span className="text-xl font-bold text-green-600 dark:text-green-400">
                    {formatAmount(booking.total_amount, booking.currency)}
                  </span>
                </div>
              </div>
            </div>

            {/* Escrow Info */}
            {hasEscrow && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 mt-4">
                <div className="flex items-start gap-3">
                  <Shield size={20} className="text-blue-500 mt-0.5" />
                  <div>
                    <div className="font-medium text-blue-600 dark:text-blue-400">
                      {formatAmount(escrowAmount, booking.currency)} held in escrow
                    </div>
                    <p className="text-sm text-secondary-text mt-1">
                      The creative's fee is held securely. After your meeting, you can release it to the creative, 
                      or it will automatically be released after 24 hours if there's no dispute.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className="card p-6 space-y-4">
            <h2 className="text-lg font-semibold text-primary-text">What's Next?</h2>
            <ul className="space-y-3 text-sm text-secondary-text">
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-500 mt-0.5" />
                <span>You'll receive a confirmation email with the booking details</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle size={16} className="text-green-500 mt-0.5" />
                <span>The creative will be notified about your booking</span>
              </li>
              {isFika && isOnline && (
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5" />
                  <span>A meeting link will be shared before the session</span>
                </li>
              )}
              {isFika && !isOnline && (
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5" />
                  <span>The creative will confirm the meeting location</span>
                </li>
              )}
              {hasEscrow && (
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-500 mt-0.5" />
                  <span>After the meeting, release the escrow from your dashboard</span>
                </li>
              )}
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/dashboard/client/bookings" 
              className="flex-1 btn btn-primary flex items-center justify-center gap-2"
            >
              <Calendar size={18} />
              View My Bookings
            </Link>
            <Link 
              to="/dashboard/messages" 
              className="flex-1 btn btn-outline flex items-center justify-center gap-2"
            >
              <MessageSquare size={18} />
              Message Creative
            </Link>
            <Link 
              to="/services" 
              className="flex-1 btn btn-outline flex items-center justify-center gap-2"
            >
              Browse More Services
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Default: Show booking details (for direct access without ?paid=1)
  if (booking) {
    return (
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="card p-6">
            <h1 className="text-xl font-semibold text-primary-text mb-4">
              Booking #{booking.booking_number || bookingId.slice(0, 8)}
            </h1>
            
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  booking.status === 'confirmed' ? 'bg-green-500/10 text-green-600' :
                  booking.status === 'pending' ? 'bg-yellow-500/10 text-yellow-600' :
                  booking.status === 'completed' ? 'bg-blue-500/10 text-blue-600' :
                  'bg-gray-500/10 text-gray-600'
                }`}>
                  {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1)}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-secondary-text">Date:</span>
                  <div className="text-primary-text">{formatDate(booking.start_at)}</div>
                </div>
                <div>
                  <span className="text-secondary-text">Time:</span>
                  <div className="text-primary-text">{formatTime(booking.start_at)} - {formatTime(booking.end_at)}</div>
                </div>
                <div>
                  <span className="text-secondary-text">Amount:</span>
                  <div className="text-primary-text font-medium">
                    {formatAmount(booking.total_amount, booking.currency)}
                  </div>
                </div>
                <div>
                  <span className="text-secondary-text">Type:</span>
                  <div className="text-primary-text">{booking.meeting_type || 'Service'}</div>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-card-border">
              <Link to="/dashboard/client/bookings" className="btn btn-primary">
                View All Bookings
              </Link>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Fallback loading
  return (
    <section className="container mx-auto px-4 py-12">
      <div className="max-w-lg mx-auto text-center space-y-4">
        <Loader2 size={40} className="animate-spin mx-auto text-blue-500" />
        <p className="text-secondary-text">Loading...</p>
      </div>
    </section>
  );
}
